<template>
  <!-- 粉丝统计 팔로워 통계 대시보드 (SPEC-CHINA-ADMIN-CERT-FOLLOWER-EP-001 팔로우, 설계시안 follower-analytics-design.html 답습) -->
  <div class="page-container-v2">
    <div class="page-header-wrapper">
      <page-header
        :title="$t('followerstats_header_title')"
        :sub-title="$t('followerstats_header_desc')"
      />
    </div>

    <!-- 데이터 가드 안내 (날짜 필드/이관분/카드 스냅샷 검증 결과) -->
    <a-alert
      type="warning"
      show-icon
      :message="$t('followerstats_data_guard_title')"
      :description="$t('followerstats_data_guard_desc')"
      style="margin-bottom: 16px;"
    />

    <!-- 공중계정 멀티선택 필터 — 플랫폼 운영자(xinlaian) 전용, 제조사는 자기 gzh 고정 -->
    <div v-if="isXinlaianOperator" class="gzh-filter-row">
      <span class="gzh-filter-label">{{ $t('followerlist_gzh_filter') }}</span>
      <a-select
        v-model:value="selectedGzh"
        mode="multiple"
        allow-clear
        size="small"
        style="min-width: 280px"
        :placeholder="$t('followerlist_all')"
        :max-tag-count="3"
        @change="handleGzhChange"
      >
        <a-select-option
          v-for="option in sortedGzhOptions"
          :key="option.gzhName"
          :value="option.gzhName"
          :label="formatGzhOptionLabel(option)"
        >
          {{ formatGzhOptionLabel(option) }}
          <span class="gzh-type-badge">{{ formatGzhTypeLabel(option) }}</span>
        </a-select-option>
      </a-select>
    </div>

    <!-- 소표본 경고 (선택 gzh 팔로워 수가 임계치 미만) -->
    <a-alert
      v-if="isXinlaianOperator && isSmallSample"
      type="info"
      show-icon
      :message="$t('followerstats_small_sample_warning')"
      style="margin: 8px 0 16px;"
    />

    <!-- KPI 4장 — 카드 내부 구조(label/value/foot)를 동일하게 통일하여 높이를 맞춘다 -->
    <a-row :gutter="16" align="stretch" style="margin-bottom: 16px;">
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card :loading="loading" class="kpi-card">
          <div class="kpi-label">{{ $t('followerstats_kpi_followers') }}</div>
          <div class="kpi-value">{{ formatNumber(kpi.followers ?? 0) }}</div>
          <div class="kpi-foot"></div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card :loading="loading" class="kpi-card">
          <div class="kpi-label">{{ $t('followerstats_kpi_card') }}</div>
          <div class="kpi-value">
            {{ formatNumber(kpi.cardHolders ?? 0) }}
            <span class="kpi-value-sub">· {{ formatPercent(kpi.cardRate) }}</span>
          </div>
          <div class="kpi-foot"></div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card :loading="loading" class="kpi-card">
          <div class="kpi-label">{{ $t('followerstats_kpi_miniapp') }}</div>
          <div class="kpi-value">{{ formatNumber(kpi.miniappUsers ?? 0) }}</div>
          <div class="kpi-foot"></div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card :loading="loading" class="kpi-card">
          <div class="kpi-label">{{ $t('followerstats_kpi_yoy') }}</div>
          <div class="kpi-value">
            {{ formatNumber(kpi.yoyCurrent ?? 0) }}
            <span v-if="typeof kpi.yoyPct === 'number'" class="kpi-delta" :class="{ up: kpi.yoyPct >= 0, down: kpi.yoyPct < 0 }">
              {{ kpi.yoyPct >= 0 ? '▲' : '▼' }}{{ formatPercent(Math.abs(kpi.yoyPct)) }}
            </span>
          </div>
          <div class="kpi-foot">{{ $t('followerstats_kpi_yoy_prev') }} {{ formatNumber(kpi.yoyPrev ?? 0) }}</div>
        </a-card>
      </a-col>
    </a-row>

    <!-- 유입 구조: 유입경로 도넛 + 월별 추이 라인 -->
    <a-row :gutter="16" style="margin-bottom: 16px;">
      <a-col :xs="24" :lg="12">
        <a-card :title="$t('followerstats_chart_scene')" :loading="loading">
          <div v-if="!loading && sceneDist.length === 0" class="chart-empty">
            <a-empty :description="$t('ecountEtl_common_noData')" />
          </div>
          <div v-else class="chart-container" role="img" :aria-label="$t('followerstats_chart_scene')">
            <Doughnut :data="sceneChartData" :options="sceneChartOptions" />
          </div>
        </a-card>
      </a-col>
      <a-col :xs="24" :lg="12">
        <a-card :title="$t('followerstats_chart_monthly')" :loading="loading">
          <div v-if="!loading && monthly.length === 0" class="chart-empty">
            <a-empty :description="$t('ecountEtl_common_noData')" />
          </div>
          <template v-else>
            <div class="chart-container" role="img" :aria-label="$t('followerstats_chart_monthly')">
              <Line :data="monthlyChartData" :options="monthlyChartOptions" />
            </div>
            <div v-if="hasPartialMonth" class="chart-caption">{{ $t('followerstats_chart_monthly_partial_note') }}</div>
          </template>
        </a-card>
      </a-col>
    </a-row>

    <!-- 전환 · 최근 활동: 채널별 전환율 바 + 일별 신규 바 -->
    <a-row :gutter="16" style="margin-bottom: 16px;">
      <a-col :xs="24" :lg="12">
        <a-card :title="$t('followerstats_chart_conversion')" :loading="loading">
          <div v-if="!loading && channelConversion.length === 0" class="chart-empty">
            <a-empty :description="$t('followerstats_conversion_not_operated')" />
          </div>
          <div v-else class="chart-container" role="img" :aria-label="$t('followerstats_chart_conversion')">
            <Bar :data="conversionChartData" :options="conversionChartOptions" />
          </div>
        </a-card>
      </a-col>
      <a-col :xs="24" :lg="12">
        <a-card :title="$t('followerstats_chart_daily')" :loading="loading">
          <div v-if="!loading && daily.length === 0" class="chart-empty">
            <a-empty :description="$t('ecountEtl_common_noData')" />
          </div>
          <div v-else class="chart-container" role="img" :aria-label="$t('followerstats_chart_daily')">
            <Bar :data="dailyChartData" :options="dailyChartOptions" />
          </div>
        </a-card>
      </a-col>
    </a-row>

    <!-- gzh별 규모 표 — 플랫폼 운영자 전용 -->
    <a-card v-if="isXinlaianOperator" :title="$t('followerstats_gzh_scale_title')" :loading="gzhScaleLoading">
      <a-table
        :columns="gzhScaleColumns"
        :data-source="gzhScaleRows"
        :pagination="false"
        :scroll="{ x: 480 }"
        size="small"
        row-key="gzhName"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'cardHolders'">
            <span v-if="record.cardHolders > 0" class="chip chip-hi">{{ formatNumber(record.cardHolders) }}</span>
            <span v-else class="chip chip-lo">{{ $t('followerstats_gzh_scale_not_operated') }}</span>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { Doughnut, Line, Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { isOperator } from '@/utils/utils';
import PageHeader from '@/views/components/PageHeader.vue';
import followerApis from './api';
import chinaInapi from '@/apis/chinaInapi';
import { GZH_LABEL_MAP, GZH_TYPE_MAP, GZH_ORDER } from './gzh-constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const SMALL_SAMPLE_THRESHOLD = 100;
const RECENT_DAYS_HIGHLIGHT = 3;

const EMPTY_STATS = {
  kpi: {},
  sceneDist: [],
  monthly: [],
  daily: [],
  channelConversion: [],
};

const DONUT_COLORS = [
  'rgba(22, 119, 255, 0.85)',
  'rgba(82, 196, 26, 0.85)',
  'rgba(250, 140, 22, 0.85)',
  'rgba(255, 77, 79, 0.85)',
  'rgba(114, 46, 209, 0.85)',
  'rgba(19, 194, 194, 0.85)',
];

export default defineComponent({
  name: 'FollowerStats',
  components: { PageHeader, Doughnut, Line, Bar },
  data() {
    return {
      apis: null,
      loading: false,
      statsData: null,
      selectedGzh: [], // 멀티선택 값(gzhName 배열). 빈 배열 = 전체
      gzhOptions: [],
      gzhScaleLoading: false,
      gzhScaleRows: [],
      gzhScaleColumns: [
        { title: this.$t('followerstats_gzh_scale_col_name'), dataIndex: 'label', key: 'label' },
        { title: this.$t('followerstats_gzh_scale_col_followers'), dataIndex: 'followers', key: 'followers', align: 'right', customRender: ({ text }) => this.formatNumber(text) },
        { title: this.$t('followerstats_gzh_scale_col_recent30d'), dataIndex: 'recent30d', key: 'recent30d', align: 'right', customRender: ({ text }) => this.formatNumber(text) },
        { title: this.$t('followerstats_gzh_scale_col_card'), key: 'cardHolders', align: 'right' },
      ],
    };
  },
  computed: {
    // 운영자 판정은 isOperator() 단일 지점 — 리터럴 비교를 두지 않는다(utils.js)
    isXinlaianOperator() {
      return isOperator();
    },
    kpi() {
      return (this.statsData || EMPTY_STATS).kpi || {};
    },
    // gzhOptions를 GZH_ORDER 순서(信徕安 → 登腾 → 登泰美)로 정렬 — 매핑에 없는 항목은 뒤로 밀림
    sortedGzhOptions() {
      return [...this.gzhOptions].sort((a, b) => {
        const ia = GZH_ORDER.indexOf(a.gzhName);
        const ib = GZH_ORDER.indexOf(b.gzhName);
        return (ia === -1 ? GZH_ORDER.length : ia) - (ib === -1 ? GZH_ORDER.length : ib);
      });
    },
    // 멀티선택 값을 comma-separated 문자열로 변환 — BE $in 조회 계약(comma string), 미선택 시 undefined(전체)
    selectedGzhParam() {
      return this.selectedGzh.length > 0 ? this.selectedGzh.join(',') : undefined;
    },
    sceneDist() {
      return (this.statsData || EMPTY_STATS).sceneDist || [];
    },
    monthly() {
      return [...((this.statsData || EMPTY_STATS).monthly || [])].sort((a, b) => (a.ym || '').localeCompare(b.ym || ''));
    },
    daily() {
      return [...((this.statsData || EMPTY_STATS).daily || [])].sort((a, b) => (a.ymd || '').localeCompare(b.ymd || ''));
    },
    channelConversion() {
      return [...((this.statsData || EMPTY_STATS).channelConversion || [])].sort((a, b) => (b.rate || 0) - (a.rate || 0));
    },
    hasPartialMonth() {
      return this.monthly.some((m) => m.partial);
    },
    isSmallSample() {
      return this.selectedGzh.length > 0 && (this.kpi.followers ?? 0) > 0 && (this.kpi.followers ?? 0) < SMALL_SAMPLE_THRESHOLD;
    },
    sceneChartData() {
      const items = this.sceneDist;
      return {
        labels: items.map((s) => s.label),
        datasets: [{
          data: items.map((s) => s.count),
          backgroundColor: items.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]),
          borderWidth: 1,
        }],
      };
    },
    sceneChartOptions() {
      const self = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'right' },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const item = self.sceneDist[ctx.dataIndex];
                if (!item) return '';
                return `${item.label}: ${self.formatNumber(item.count)} (${self.formatPercent(item.pct)})`;
              },
            },
          },
        },
      };
    },
    monthlyChartData() {
      const items = this.monthly;
      return {
        labels: items.map((m) => (m.partial ? `${m.ym}*` : m.ym)),
        datasets: [{
          label: this.$t('followerstats_chart_monthly'),
          data: items.map((m) => m.count),
          borderColor: '#1677ff',
          backgroundColor: 'rgba(22, 119, 255, 0.1)',
          fill: true,
          tension: 0.25,
          pointRadius: items.map((m) => (m.partial ? 5 : 2)),
          pointBackgroundColor: items.map((m) => (m.partial ? '#faad14' : '#1677ff')),
        }],
      };
    },
    monthlyChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      };
    },
    conversionChartData() {
      const items = this.channelConversion;
      return {
        labels: items.map((c) => c.label),
        datasets: [{
          label: this.$t('followerstats_chart_conversion'),
          data: items.map((c) => c.rate),
          backgroundColor: 'rgba(22, 119, 255, 0.85)',
          borderColor: 'rgba(22, 119, 255, 1)',
          borderWidth: 1,
        }],
      };
    },
    conversionChartOptions() {
      const self = this;
      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const item = self.channelConversion[ctx.dataIndex];
                if (!item) return '';
                return `${self.formatPercent(item.rate)} (${self.formatNumber(item.cards)}/${self.formatNumber(item.followers)})`;
              },
            },
          },
        },
        scales: {
          x: { beginAtZero: true, ticks: { callback: (v) => `${v}%` } },
        },
      };
    },
    dailyChartData() {
      const items = this.daily;
      const highlightFrom = Math.max(0, items.length - RECENT_DAYS_HIGHLIGHT);
      return {
        labels: items.map((d) => d.ymd),
        datasets: [{
          label: this.$t('followerstats_chart_daily'),
          data: items.map((d) => d.count),
          backgroundColor: items.map((_, i) => (i >= highlightFrom ? 'rgba(82, 196, 26, 0.85)' : 'rgba(22, 119, 255, 0.85)')),
          borderRadius: 2,
        }],
      };
    },
    dailyChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { maxTicksLimit: 10, font: { size: 10 } } },
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      };
    },
  },
  async created() {
    this.apis = followerApis(chinaInapi);
  },
  async mounted() {
    if (this.isXinlaianOperator) {
      await this.loadGzhOptions();
    }
    await this.fetchStats();
    if (this.isXinlaianOperator) {
      await this.loadGzhScaleTable();
    }
  },
  methods: {
    async loadGzhOptions() {
      try {
        const response = await this.apis.getGzhOptions();
        if (response.data.success) {
          this.gzhOptions = response.data.data.options || [];
        }
      } catch (error) {
        console.error('Error fetching gzh options:', error);
      }
    },
    formatGzhOptionLabel(option) {
      return GZH_LABEL_MAP[option.gzhName] || option.label;
    },
    formatGzhTypeLabel(option) {
      const type = GZH_TYPE_MAP[option.gzhName];
      if (type === 'svc') return this.$t('followerstats_gzh_type_service');
      return this.$t('followerstats_gzh_type_subscribe');
    },
    async handleGzhChange() {
      await this.fetchStats();
    },
    async fetchStats() {
      try {
        this.loading = true;
        const response = await this.apis.getFollowerStats(this.selectedGzhParam);
        if (response.data.success) {
          this.statsData = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching follower stats:', error);
      } finally {
        this.loading = false;
      }
    },
    // 공중계정별 규모 표 — 통계 EP가 gzhName 스코프 응답만 제공하므로 옵션별로 개별 조회해 조합한다
    // (gzh 계정 수가 소수(3개)라 N+1 호출 비용이 낮음 — SPEC 계약에 별도 breakdown 필드가 없음)
    async loadGzhScaleTable() {
      if (this.gzhOptions.length === 0) return;
      try {
        this.gzhScaleLoading = true;
        const rows = await Promise.all(
          this.gzhOptions.map(async (option) => {
            try {
              const response = await this.apis.getFollowerStats(option.gzhName);
              const data = response.data.success ? response.data.data : EMPTY_STATS;
              const recent30d = (data.daily || []).slice(-30).reduce((sum, d) => sum + (d.count || 0), 0);
              return {
                gzhName: option.gzhName,
                label: this.formatGzhOptionLabel(option),
                followers: data.kpi?.followers ?? option.count ?? 0,
                recent30d,
                cardHolders: data.kpi?.cardHolders ?? 0,
              };
            } catch (error) {
              console.error(`Error fetching stats for gzh ${option.gzhName}:`, error);
              return {
                gzhName: option.gzhName,
                label: this.formatGzhOptionLabel(option),
                followers: option.count ?? 0,
                recent30d: 0,
                cardHolders: 0,
              };
            }
          })
        );
        this.gzhScaleRows = rows.sort((a, b) => b.followers - a.followers);
      } finally {
        this.gzhScaleLoading = false;
      }
    },
    formatNumber(v) {
      if (v === null || v === undefined || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      return n.toLocaleString(this.$i18n?.locale || 'zh-CN', { maximumFractionDigits: 0 });
    },
    formatPercent(v) {
      if (v === null || v === undefined || Number.isNaN(Number(v))) return '-';
      return `${Number(v).toFixed(1)}%`;
    },
  },
});
</script>

<style lang="scss" scoped>
.page-header-wrapper {
  margin-bottom: 16px;
}

.gzh-filter-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.gzh-filter-label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
}

.gzh-type-badge {
  font-size: 10px;
  margin-left: 4px;
  color: rgba(0, 0, 0, 0.45);
}

.kpi-card {
  height: 100%;

  :deep(.ant-card-body) {
    display: flex;
    flex-direction: column;
    min-height: 84px;
  }
}

.kpi-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 24px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  .kpi-value-sub {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 500;
  }
}

.kpi-delta {
  font-size: 14px;
  font-weight: 600;
  margin-left: 6px;

  &.up { color: #1baf7a; }
  &.down { color: #f5222d; }
}

.kpi-foot {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 2px;
  min-height: 18px;
}

.chart-container {
  height: 240px;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.chart-caption {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 8px;
}

.chip {
  display: inline-block;
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 999px;
  font-weight: 600;
}

.chip-hi {
  background: #d5e8e6;
  color: #0e7c7b;
}

.chip-lo {
  background: #f0f0f0;
  color: rgba(0, 0, 0, 0.45);
}
</style>
